import { Flex, Icon, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, PhoneIncoming } from 'lucide-react'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DoNotDisturb, useSettingsState } from '~/atoms/SettingsState'
import {
  Menu,
  MenuPlacement,
  MenuPositioningWrapper,
} from '~/components/shared/Dropdown/Menu'
import { MenuItem } from '~/components/shared/Dropdown/MenuItem'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { castRef } from '~/utils/refUtils'

import {
  DoNotDisturbSplitButtonVariants,
  useDoNotDisturbSplitButtonStyle,
} from './componentStyles'
import { DoNotDisturbSplitButtonStyleValues } from './componentStyles/DoNotDisturbSplitButton.styles'

interface DoNotDisturbState {
  title: string
  subtitle?: string
  enabled: boolean
}

const getDoNotDisturbState = (
  doNotDisturbTimestamp: Date | null | undefined,
  t: (key: string, options?: any) => string,
): DoNotDisturbState => {
  if (doNotDisturbTimestamp === undefined) {
    return {
      title: t('webPhone.receivingCalls'),
      enabled: false,
    }
  }
  const state: DoNotDisturbState = {
    title: t('webPhone.doNotDisturb.title'),
    enabled: true,
  }
  if (doNotDisturbTimestamp) {
    const timeString = new Date(doNotDisturbTimestamp).toLocaleTimeString(
      'en-US',
      { hour: 'numeric', minute: '2-digit' },
    )
    state.subtitle = t('webPhone.doNotDisturb.until', { time: timeString })
  }

  return state
}

const getDoNotDisturbVariant = (isDoNotDisturbEnabled: boolean) => {
  if (!isDoNotDisturbEnabled) {
    return undefined
  }
  return DoNotDisturbSplitButtonVariants.DoNotDisturb
}

export const DoNotDisturbSplitButton = () => {
  const { t } = useTranslation()
  const {
    settings: {
      webPhone: { doNotDisturbTimestamp },
    },
    canUseWebPhone,
    setDoNotDisturb,
  } = useSettingsState()

  const [isMenuVisible, setIsMenuVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const doNotDisturbState = getDoNotDisturbState(doNotDisturbTimestamp, t)

  const variant = useMemo(() => {
    return getDoNotDisturbVariant(doNotDisturbState.enabled)
  }, [doNotDisturbState.enabled])

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      setIsMenuVisible(false)
    },
  })

  const handleLeftButtonClick = () => {
    if (doNotDisturbState.enabled) {
      setDoNotDisturb(undefined)
    } else {
      setDoNotDisturb(DoNotDisturb.INDEFINITE)
    }
  }

  const handleMenuItemClick = (value: number | undefined) => {
    setDoNotDisturb(value)
    setIsMenuVisible(false)
  }

  const menuItems: MenuItem<number | undefined>[] = [
    {
      content: t('webPhone.doNotDisturb.indefinite'),
      value: DoNotDisturb.INDEFINITE,
    },
    {
      content: t('webPhone.doNotDisturb.for30Minutes'),
      value: DoNotDisturb.HALF_HOUR,
    },
    {
      content: t('webPhone.doNotDisturb.for1Hour'),
      value: DoNotDisturb.ONE_HOUR,
    },
    {
      content: t('webPhone.doNotDisturb.for2Hours'),
      value: DoNotDisturb.TWO_HOURS,
    },
    {
      content: t('webPhone.doNotDisturb.untilTomorrow'),
      value: DoNotDisturb.TOMORROW,
    },
  ]

  if (doNotDisturbState.enabled) {
    menuItems.unshift({
      content: t('webPhone.doNotDisturb.turnOff'),
      value: undefined,
    })
  }

  const styles = useDoNotDisturbSplitButtonStyle({ variant: variant })
  return !canUseWebPhone ? (
    <></>
  ) : (
    <Flex css={styles.container}>
      <Flex asChild css={styles.leftButton}>
        <button type="button" onClick={handleLeftButtonClick}>
          <Icon css={styles.doNotDisturbIcon} as={PhoneIncoming} aria-hidden />
          <Flex as="span" css={styles.leftButtonTextWrapper}>
            <Text as="span" css={styles.titleText}>
              {doNotDisturbState.title}
            </Text>
            {doNotDisturbState.subtitle && (
              <Text as="span" css={styles.subtitleText}>
                {doNotDisturbState.subtitle}
              </Text>
            )}
          </Flex>
        </button>
      </Flex>
      <MenuPositioningWrapper ref={containerRef}>
        <Flex
          ref={buttonRef}
          css={styles.rightButton}
          onClick={() => setIsMenuVisible((prev) => !prev)}
        >
          <Icon
            css={styles.chevronIcon}
            as={isMenuVisible ? ChevronUp : ChevronDown}
          />
        </Flex>
        {isMenuVisible && (
          <Menu
            anchorRef={castRef(buttonRef)}
            items={menuItems}
            onSelect={handleMenuItemClick}
            options={{
              width: DoNotDisturbSplitButtonStyleValues.menuWidth,
              placement: MenuPlacement.BottomRight,
            }}
          />
        )}
      </MenuPositioningWrapper>
    </Flex>
  )
}
